import { useEffect, useRef, useState } from "react";
import { Send, ImagePlus, X, Check, CheckCheck, Loader2 } from "lucide-react";
import { API } from "@/lib/api";

export const WA = { bg: "#0b141a", panel: "#111b21", head: "#202c33", out: "#005c4b", in: "#202c33", green: "#00a884", muted: "#8696a0" };

export const chatTime = (iso) => {
  const d = new Date(iso), now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const y = new Date(now); y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return "Yesterday";
  return d.toLocaleDateString();
};

export const Avatar = ({ name, picture, size = 40 }) => (
  picture ? <img src={picture} alt="" className="shrink-0 rounded-full object-cover" style={{ width: size, height: size }} />
    : <div className="flex shrink-0 items-center justify-center rounded-full font-bold text-white" style={{ width: size, height: size, background: "#6b7c85", fontSize: size * 0.4 }}>{(name || "?").slice(0, 1).toUpperCase()}</div>
);

// Message bubbles; `mine(m)` decides which side a message sits on.
export const MessageList = ({ messages, mine, testId = "chat-messages" }) => {
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [messages.length]);
  return (
    <div className="flex-1 space-y-1.5 overflow-y-auto px-4 py-3" style={{ background: WA.bg }} data-testid={testId}>
      {messages.map((m) => {
        const me = mine(m);
        return (
          <div key={m.id} className={`flex ${me ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
            <div className="max-w-[78%] rounded-lg px-2 py-1.5 text-[14px] text-[#e9edef] shadow" style={{ background: me ? WA.out : WA.in }}>
              {m.has_image && <a href={`${API}/chat/image/${m.id}`} target="_blank" rel="noreferrer"><img src={`${API}/chat/image/${m.id}`} alt="attachment" className="mb-1 max-h-60 rounded-md" /></a>}
              {m.text && <span className="whitespace-pre-wrap break-words">{m.text}</span>}
              <span className="ml-2 inline-flex items-center gap-0.5 align-bottom text-[10px]" style={{ color: WA.muted }}>
                {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                {me && ((m.from_admin ? m.read_by_user : m.read_by_admin) ? <CheckCheck size={13} className="text-[#53bdeb]" /> : <Check size={13} />)}
              </span>
            </div>
          </div>
        );
      })}
      <div ref={end} />
    </div>
  );
};

export const Composer = ({ onSend, testId = "chat" }) => {
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const send = async () => {
    if ((!text.trim() && !file) || busy) return;
    setBusy(true);
    try { await onSend(text.trim(), file); setText(""); setFile(null); } finally { setBusy(false); }
  };
  return (
    <div style={{ background: WA.head }}>
      {file && <div className="flex items-center gap-2 px-3 pt-2 text-xs text-[#e9edef]" data-testid={`${testId}-attachment`}><ImagePlus size={14} /> <span className="truncate">{file.name}</span><button onClick={() => setFile(null)} className="text-[#8696a0] hover:text-white"><X size={14} /></button></div>}
      <div className="flex items-end gap-2 px-3 py-2">
        <button onClick={() => fileRef.current?.click()} data-testid={`${testId}-photo-btn`} className="p-2 text-[#8696a0] hover:text-white" aria-label="Attach photo"><ImagePlus size={20} /></button>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" data-testid={`${testId}-photo-input`} onChange={(e) => { setFile(e.target.files?.[0] || null); e.target.value = ""; }} />
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={1} placeholder="Type a message" data-testid={`${testId}-input`}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          className="max-h-28 flex-1 resize-none rounded-lg px-3 py-2 text-sm text-[#e9edef] outline-none placeholder:text-[#8696a0]" style={{ background: "#2a3942" }} />
        <button onClick={send} disabled={busy} data-testid={`${testId}-send-btn`} className="flex h-10 w-10 items-center justify-center rounded-full text-white disabled:opacity-50" style={{ background: WA.green }} aria-label="Send">
          {busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
        </button>
      </div>
    </div>
  );
};
